/**
 * palais-product.js
 *
 * Called by: sections/product-main.liquid
 *
 * Responsibilities:
 *   1. Image gallery: swap the visible main image when a thumbnail or dot is clicked.
 *   2. Add-to-Cart: AJAX POST to /cart/add.js, then refresh the cart drawer and open it.
 *      Falls back to a full page submit if AJAX fails.
 *   3. Mobile sticky ATC bar: hide while the real ATC is visible, show after the user
 *      scrolls past it. Forwards click to the real form submit.
 *   4. Accordion details: native <details> elements; nothing JS-side needed.
 *
 *   The cart drawer itself is owned by snippets/palais-cart-drawer.liquid; this file
 *   only triggers it via window.PalaisCart.open() / refresh().
 */

(function () {
  'use strict';

  // ---------- Image gallery (mobile dot pager) ----------
  // Desktop is a vertical stack — no JS needed. On mobile the stack becomes a
  // horizontal swipe carousel; we update the active dot from scroll position
  // and let dot clicks jump to the matching image.
  function initGallery(root) {
    var stack = root.querySelector('[data-palais-gallery-stack]');
    var dots = root.querySelectorAll('[data-palais-gallery-dot]');
    if (!stack || dots.length === 0) return;

    var imgs = stack.querySelectorAll('.palais-pdp-gallery__img');

    function setActive(idx) {
      dots.forEach(function (btn) {
        var btnIdx = parseInt(btn.getAttribute('data-index'), 10);
        btn.setAttribute('aria-pressed', String(btnIdx === idx));
      });
    }

    // Click a dot → scroll the stack to the matching image.
    dots.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = parseInt(btn.getAttribute('data-index'), 10);
        var target = imgs[idx];
        if (!target) return;
        // Smooth horizontal scroll on mobile; desktop stack ignores this (no overflow).
        stack.scrollTo({ left: target.offsetLeft, behavior: 'smooth' });
      });
    });

    // Watch which image is centred in the mobile carousel and reflect it in the dots.
    if ('IntersectionObserver' in window) {
      var io = new IntersectionObserver(function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
            var target = entry.target;
            var idx = parseInt(target.getAttribute('data-index'), 10);
            if (!isNaN(idx)) setActive(idx);
          }
        });
      }, { root: stack, threshold: [0, 0.6, 1] });
      imgs.forEach(function (img) { io.observe(img); });
    }
  }

  // ---------- Add-to-Cart + "Added to your cart" state ----------
  // These are one-of-a-kind pieces (qty 1): once the item is in the cart there
  // is nothing more to add, so the button locks into a disabled "Added to your
  // cart" state. That state is reconciled three ways:
  //   1. Server-rendered on load (product-main.liquid checks cart.items).
  //   2. A fresh /cart.js check on boot, in case the PDP was served from
  //      Shopify's full-page cache with a stale button.
  //   3. The 'palais:cart:change' event the cart drawer fires on every add /
  //      remove — so removing the item in the drawer re-enables the button.
  function initAtc(section) {
    var form = section.querySelector('[data-palais-pdp-form]');
    if (!form) return;
    var atcBtn = form.querySelector('[data-palais-atc]');
    var stickyBtn = document.querySelector('[data-palais-sticky-atc-btn]');

    function variantId() {
      var input = form.querySelector('input[name="id"]');
      if (input && input.value) return String(input.value);
      return String(section.getAttribute('data-variant-id') || '');
    }

    function inCart(cart) {
      if (!cart || !cart.items) return false;
      var vid = variantId();
      return cart.items.some(function (it) {
        return String(it.variant_id) === vid || String(it.id) === vid;
      });
    }

    // Apply the added / not-added state to a button. Never re-enables a
    // sold-out button (data-sold-out), and restores each button's own default
    // label (the sticky button's includes the price) from data-label-default.
    function applyAdded(btn, added) {
      if (!btn || btn.getAttribute('data-sold-out') === 'true') return;
      if (added) {
        btn.textContent = 'Added to your cart';
        btn.disabled = true;
        btn.classList.add('is-added');
      } else {
        btn.textContent = btn.getAttribute('data-label-default') || 'Add to Cart';
        btn.disabled = false;
        btn.classList.remove('is-added');
      }
    }

    function markAdded(added) {
      applyAdded(atcBtn, added);
      applyAdded(stickyBtn, added);
    }

    function setBusy(busy) {
      [atcBtn, stickyBtn].forEach(function (btn) {
        if (!btn || btn.getAttribute('data-sold-out') === 'true') return;
        if (busy) {
          btn.setAttribute('aria-busy', 'true');
          btn.textContent = 'Adding…';
        } else {
          btn.removeAttribute('aria-busy');
        }
      });
    }

    function submit() {
      // Already in cart / busy / sold out — nothing to do.
      if (atcBtn && (atcBtn.disabled || atcBtn.getAttribute('aria-busy') === 'true')) return;
      setBusy(true);

      var formData = new FormData(form);

      fetch((window.routes && window.routes.cart_add_url) || '/cart/add.js', {
        method: 'POST',
        headers: { 'Accept': 'application/javascript', 'X-Requested-With': 'XMLHttpRequest' },
        body: formData
      })
        .then(function (res) {
          if (!res.ok) throw new Error('Add to cart failed (' + res.status + ')');
          return res.json();
        })
        .then(function () {
          if (window.PalaisCart && typeof window.PalaisCart.refresh === 'function') {
            return window.PalaisCart.refresh();
          }
        })
        .then(function () {
          setBusy(false);
          markAdded(true);   // lock the button — there is nothing more to add
          if (window.PalaisCart && typeof window.PalaisCart.open === 'function') {
            window.PalaisCart.open();
          }
        })
        .catch(function (err) {
          // Fall back to a real form submit so the customer isn't stuck.
          // eslint-disable-next-line no-console
          console.warn('[Palais ATC] AJAX failed, falling back to page submit:', err);
          setBusy(false);
          form.submit();
        });
    }

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      submit();
    });

    if (stickyBtn) {
      stickyBtn.addEventListener('click', function () {
        if (stickyBtn.disabled) return;
        submit();
      });
    }

    // Re-sync whenever the cart changes anywhere (add, qty change, remove).
    document.addEventListener('palais:cart:change', function (e) {
      markAdded(inCart(e.detail));
    });

    // Reconcile against the live cart on load: the server-rendered state can be
    // stale if Shopify served this PDP from its full-page cache.
    fetch('/cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) { markAdded(inCart(cart)); })
      .catch(function () { /* keep the server-rendered state */ });
  }

  // ---------- Mobile sticky ATC visibility ----------
  function initStickyAtc(section) {
    var bar = document.querySelector('[data-palais-sticky-atc]');
    var realAtc = section.querySelector('[data-palais-atc]');
    if (!bar || !realAtc) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        // Show sticky bar only when real ATC is offscreen.
        bar.setAttribute('data-visible', String(!entry.isIntersecting));
      });
    }, { threshold: 0.05 });
    io.observe(realAtc);

    document.body.classList.add('palais--pdp');   // adds bottom padding via CSS
  }

  function boot() {
    var section = document.querySelector('[data-palais-pdp]');
    if (!section) return;
    initGallery(section);
    initAtc(section);
    initStickyAtc(section);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
